import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { calcClientDerivatives } from '@shared';
import {
  summarizeCareScenario,
  withCareScenarioChange,
  type CareScenarioChange,
} from '@shared/consumerPlan';
import {
  previewClientUpdates,
  putCareEnvironmentSelectionsForClientByClientIdRequest,
  selectMaybeClientPartner,
} from '@/model';
import { consumerApi, consumerQuoteKeys } from '../api/consumer-api';

/**
 * Previews and saves changes to the consumer's care plan through the same endpoints the
 * edit-assumptions screen uses, so every other part of the app sees the same scenario.
 */
export function useCarePlanEditor(client: Client | null) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const clientPartner = useSelector(selectMaybeClientPartner);

  // The server prices the quote list from the saved care plan, so it's refreshed once this lands.
  const { mutate: saveCustomInferences } = useMutation({
    mutationFn: (variables: { clientId: string; changes: Pick<CareScenarioChange, 'careHoursMultiplier' | 'familyCareShare'> }) =>
      consumerApi.saveCustomInferences(variables.clientId, variables.changes),
    onSuccess: (_saved, { clientId }) => queryClient.invalidateQueries({ queryKey: consumerQuoteKeys.detail(clientId) }),
  });

  const derive = useCallback(
    (changed: Client) => calcClientDerivatives(changed, clientPartner) as Client,
    [clientPartner],
  );

  const previewScenario = useCallback(
    (change: CareScenarioChange) =>
      client ? summarizeCareScenario(derive(withCareScenarioChange(client, change))) : null,
    [client, derive],
  );

  const saveScenario = useCallback(
    (change: CareScenarioChange) => {
      if (!client) return;
      const { clientId } = client;
      const changed = withCareScenarioChange(client, change);

      dispatch(previewClientUpdates(derive(changed)));
      if (change.careSetting) {
        const { careEnvironmentSelections } = changed;
        dispatch(putCareEnvironmentSelectionsForClientByClientIdRequest({ clientId, careEnvironmentSelections }));
      }
      const { careHoursMultiplier, familyCareShare } = change;
      if (careHoursMultiplier !== undefined || familyCareShare !== undefined) {
        saveCustomInferences({ clientId, changes: { careHoursMultiplier, familyCareShare } });
      }
    },
    [client, derive, dispatch, saveCustomInferences],
  );

  return { previewScenario, saveScenario };
}
